/**
 * No-login organizer form - client accesses via secure link (token in URL).
 * Uses /organizer-secure/:token/ API endpoints (no auth required).
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const OrganizerSecureOrganizer = () => {
  const { token } = useParams();
  const [organizer, setOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const api = axios.create({ baseURL: API_BASE });

  useEffect(() => {
    if (token) fetchOrganizer();
  }, [token]);

  const fetchOrganizer = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/organizer-secure/${token}/`);
      if (response.data.success && response.data.data) {
        setOrganizer(response.data.data);
        const first = response.data.data.sections?.[0];
        if (first) setActiveSection(first.section_type);
      } else {
        setError('Failed to load organizer');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const getFieldValue = (field) => {
    const r = field.response;
    if (!r) return '';
    if (r.text_value !== undefined && r.text_value !== null && r.text_value !== '') return r.text_value;
    if (r.number_value !== undefined && r.number_value !== null) return r.number_value;
    if (r.boolean_value === true) return true;
    if (r.boolean_value === false) return false;
    if (r.date_value) return r.date_value;
    return '';
  };

  const getFieldValueById = (fieldId) => {
    for (const section of organizer?.sections || []) {
      for (const f of section.fields || []) {
        if (f.id === fieldId) return getFieldValue(f);
      }
    }
    return '';
  };

  const isFieldVisible = (field) => {
    const depFieldId = field.conditional_field_id ?? field.conditional_field;
    if (!depFieldId) return true;
    const depValue = getFieldValueById(depFieldId);
    const expected = field.conditional_value;
    if (expected === undefined || expected === null) return true;
    if (Array.isArray(expected)) return expected.some((v) => String(depValue) === String(v) || depValue === v);
    return String(depValue) === String(expected) || depValue === expected;
  };

  const handleResponseChange = async (fieldId, value) => {
    try {
      setSaving(true);
      const payload = { field: fieldId };
      if (typeof value === 'boolean') payload.boolean_value = value;
      else if (typeof value === 'number') payload.number_value = value;
      else if (value && typeof value === 'object' && value instanceof Date) payload.date_value = value.toISOString().split('T')[0];
      else payload.text_value = String(value ?? '');
      const response = await api.post(`/organizer-secure/${token}/responses/`, payload);
      if (response.data.success && organizer) {
        setOrganizer((prev) => {
          const next = { ...prev };
          next.sections = next.sections.map((sec) => ({
            ...sec,
            fields: sec.fields.map((f) =>
              f.id === fieldId
                ? { ...f, response: response.data.data }
                : f
            ),
          }));
          return next;
        });
      }
    } catch (err) {
      console.error('Error saving response:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await api.post(`/organizer-secure/${token}/submit/`);
      if (response.data.success) {
        setSubmitted(true);
      } else {
        setError('Failed to submit organizer');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit organizer');
    }
  };

  const renderField = (field) => {
    const value = getFieldValue(field);
    switch (field.field_type) {
      case 'text':
      case 'email':
        return (
          <input
            type={field.field_type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={field.is_required}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleResponseChange(field.id, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={field.is_required}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={field.is_required}
          />
        );
      case 'boolean':
        return (
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name={`field_${field.id}`}
                checked={value === true}
                onChange={() => handleResponseChange(field.id, true)}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`field_${field.id}`}
                checked={value === false}
                onChange={() => handleResponseChange(field.id, false)}
                className="mr-2"
              />
              No
            </label>
          </div>
        );
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={field.is_required}
          >
            <option value="">Select an option</option>
            {(field.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={field.is_required}
          />
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={field.is_required}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Organizer</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-500 mt-4">This link may have expired. Please contact your tax preparer for a new link.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-green-700 mb-2">Thank You!</h2>
          <p className="text-gray-600">Your tax organizer has been submitted successfully.</p>
          <p className="text-sm text-gray-500 mt-4">We will be in touch if we need any additional information.</p>
        </div>
      </div>
    );
  }

  if (!organizer || !organizer.sections?.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Organizer not found.</p>
      </div>
    );
  }

  const currentSection = organizer.sections.find((s) => s.section_type === activeSection);
  const progress = organizer.progress_percentage || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{organizer.name}</h1>
              <p className="text-gray-600">
                {organizer.organizer_type_name} • Tax Year {organizer.tax_year}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {saving && <span className="text-sm text-gray-500">Saving...</span>}
            </div>
          </div>
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          <div className="w-64 bg-white rounded-lg shadow-sm p-6 mr-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{organizer.organizer_type_name} Organizer</h3>
            <div className="space-y-2">
              {organizer.sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.section_type)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                    activeSection === section.section_type ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 bg-white rounded-lg shadow-sm p-8">
            {currentSection && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{currentSection.title}</h2>
                {currentSection.description && <p className="text-gray-600 mb-8">{currentSection.description}</p>}
                <div className="space-y-6">
                  {currentSection.fields.filter(isFieldVisible).map((field) => (
                    <div key={field.id} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                        {field.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.help_text && <p className="text-sm text-gray-500">{field.help_text}</p>}
                      {renderField(field)}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      const idx = organizer.sections.findIndex((s) => s.section_type === activeSection);
                      if (idx > 0) setActiveSection(organizer.sections[idx - 1].section_type);
                    }}
                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Previous
                  </button>
                  <div className="flex space-x-4">
                    {progress >= 100 && (
                      <button
                        onClick={handleSubmit}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Submit Organizer
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const idx = organizer.sections.findIndex((s) => s.section_type === activeSection);
                        if (idx < organizer.sections.length - 1) setActiveSection(organizer.sections[idx + 1].section_type);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizerSecureOrganizer;
